import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { AuthorityBadge } from "@/components/authority-badge";
import {
  Globe,
  FileText,
  CheckCircle2,
  ExternalLink,
  Plus,
} from "lucide-react";
import type { AuthorityLevel, DiscoveryCandidate } from "@iamu/shared";

// ─── CandidateCard ───────────────────────────────────────────────────

export function CandidateCard({
  candidate,
  index,
  isIngested,
  onIngest,
  projectId,
  sourceId,
}: {
  candidate: DiscoveryCandidate;
  index: number;
  isIngested: boolean;
  onIngest?: () => void;
  projectId?: string;
  sourceId?: string;
}) {
  const isAlreadyIngested = candidate.deduplication.type === "exact_url";
  const canIngest = !isAlreadyIngested && !candidate.isPdf && !isIngested;

  const truncatedUrl = (() => {
    try {
      const u = new URL(candidate.url);
      const path =
        u.pathname.length > 40 ? u.pathname.slice(0, 40) + "..." : u.pathname;
      return u.hostname + path;
    } catch {
      return candidate.url.slice(0, 60);
    }
  })();

  return (
    <div
      className={cn(
        "group flex items-start gap-3 rounded-lg border p-3 transition-all",
        "animate-in fade-in-0 slide-in-from-bottom-2 duration-300",
        isAlreadyIngested && "opacity-50",
        isIngested && "border-mark-helps/30 bg-mark-helps/8",
      )}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-tight">
              {candidate.suggestedName}
            </p>
            <a
              href={candidate.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors truncate"
              onClick={(e) => e.stopPropagation()}
            >
              <Globe className="h-3 w-3 shrink-0" />
              {truncatedUrl}
              <ExternalLink className="h-2.5 w-2.5 shrink-0" />
            </a>
          </div>
        </div>

        {/* Authority & Metadata row */}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <AuthorityBadge
            level={candidate.suggestedAuthorityLevel as AuthorityLevel}
          />
          <span className="tabular-nums font-medium text-foreground">
            {Math.round(candidate.relevanceScore * 100)}% relevant
          </span>
          <span aria-hidden="true">·</span>
          <span>
            {candidate.isPrimarySource ? "Primary source" : "Secondary source"}
          </span>
          {candidate.isPdf && (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1 text-severity-important">
                <FileText className="h-3 w-3" />
                PDF
              </span>
            </>
          )}
        </div>

        {candidate.deduplication.type === "similar_name" && (
          <p className="mt-1 text-xs text-muted-foreground">
            Possibly related to {candidate.deduplication.existingSourceName}
          </p>
        )}

        {/* Relevance explanation */}
        <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
          {candidate.relevanceExplanation}
        </p>

        {/* Ingested state or Add button */}
        {isIngested || isAlreadyIngested ? (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-finding-helps">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>Ingested</span>
            {projectId && sourceId && (
              <Link
                to={`/projects/${projectId}/sources/${sourceId}`}
                className="ml-1 underline hover:no-underline"
              >
                View source
              </Link>
            )}
          </div>
        ) : canIngest ? (
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onIngest?.();
              }}
              aria-label={`Add ${candidate.suggestedName}`}
            >
              <Plus className="h-3.5 w-3.5" />
              Add source
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
